/* ============================================================
   Non-tariff barriers — taxonomy and submission formatting.

   The AfCFTA online reporting mechanism at tradebarriers.africa had
   only about 1,900 registered users across the whole continent as at
   January 2026. Barriers that go unreported are never resolved, and the
   people best placed to report them are drivers and clearing agents who
   are not going to open a web portal at a weighbridge at 2am.

   What this module does is lower that cost: capture the barrier in
   structured form on the phone in the moment, then produce a properly
   formatted submission the operator can lodge later.

   What it does NOT do is submit on the user's behalf. There is no
   backend, and pretending otherwise would be worse than useless — an
   operator who believes a report was filed when it was not is worse off
   than one who knows they still have to file it.
   ============================================================ */

/** EAC / AfCFTA NTB categories, as used by the official mechanism. */
export const NTB_CATEGORIES = {
  A: {
    label: 'Government participation in trade',
    hint: 'State trading, subsidies, or restrictive practices tolerated by government.'
  },
  B: {
    label: 'Customs & administrative entry procedures',
    hint: 'Valuation disputes, classification disputes, clearance delays, duplicated checks.'
  },
  C: {
    label: 'Technical barriers to trade',
    hint: 'Standards, testing or certification not recognised across the border.'
  },
  D: {
    label: 'Sanitary & phytosanitary measures',
    hint: 'Health, plant or veterinary certificates rejected or re-demanded.'
  },
  E: {
    label: 'Specific limitations',
    hint: 'Quotas, licensing requirements, export or import bans.'
  },
  F: {
    label: 'Charges on imports',
    hint: 'Fees or levies not published, not receipted, or applied inconsistently.'
  },
  G: {
    label: 'Other procedural problems',
    hint: 'Limited operating hours, missing staff, systems down, arbitrary requirements.'
  },
  H: {
    label: 'Transport, clearing & forwarding',
    hint: 'Weighbridge disputes, axle-load inconsistency, escorts, roadblocks, transit bonds.'
  }
};

/** Concrete barrier types seen on the Northern and Central Corridors. */
export const BARRIER_TYPES = [
  { id: 'weighbridge', category: 'H', label: 'Weighbridge delay or dispute' },
  { id: 'axle-load', category: 'H', label: 'Axle-load limit inconsistency between states' },
  { id: 'roadblock', category: 'H', label: 'Roadblock or police check delay' },
  { id: 'escort', category: 'H', label: 'Mandatory escort requirement' },
  { id: 'transit-bond', category: 'H', label: 'Transit bond delay or duplication' },
  { id: 'clearance-delay', category: 'B', label: 'Customs clearance delay' },
  { id: 'valuation', category: 'B', label: 'Customs valuation dispute' },
  { id: 'classification', category: 'B', label: 'HS classification dispute' },
  { id: 'duplicate-inspection', category: 'B', label: 'Duplicated inspection at a second point' },
  { id: 'cert-rejection', category: 'C', label: 'Certificate or standard not recognised' },
  { id: 'sps', category: 'D', label: 'SPS certificate rejected or re-demanded' },
  { id: 'undocumented-fee', category: 'F', label: 'Fee charged without a published tariff' },
  { id: 'no-receipt', category: 'F', label: 'Payment demanded without a receipt' },
  { id: 'hours', category: 'G', label: 'Border or office closed within stated hours' },
  { id: 'system-down', category: 'G', label: 'Government system unavailable' },
  { id: 'licence', category: 'E', label: 'Permit or licence demanded unexpectedly' },
  { id: 'other', category: 'G', label: 'Other' }
];

export function barrierType(id) {
  return BARRIER_TYPES.find((t) => t.id === id) || null;
}

/** Common reporting points on the corridors this product covers. */
export const LOCATIONS = [
  'Mombasa Port',
  'Mombasa — Nairobi highway (A109)',
  'Mariakani weighbridge',
  'Athi River weighbridge',
  'Gilgil weighbridge',
  'Nairobi ICD (Embakasi)',
  'Naivasha ICD',
  'Malaba border (KE/UG)',
  'Busia border (KE/UG)',
  'Namanga border (KE/TZ)',
  'Taveta border (KE/TZ)',
  'Isebania border (KE/TZ)',
  'Gatuna / Katuna border (UG/RW)',
  'Rusumo border (TZ/RW)',
  'Elegu / Nimule border (UG/SS)',
  'Other'
];

export const AGENCIES = [
  'KRA Customs',
  'KEBS',
  'KEPHIS',
  'NTSA',
  'KPA',
  'Kenya Police',
  'Port Health',
  'County government',
  'Immigration',
  'Partner state authority',
  'Unclear / not stated',
  'Other'
];

/**
 * Format a barrier for lodging at tradebarriers.africa.
 * Plain text, because that is what pastes cleanly into a web form.
 */
export function formatSubmission(barrier) {
  const type = barrierType(barrier.typeId);
  const category = type ? NTB_CATEGORIES[type.category] : null;

  const lines = [
    'NON-TARIFF BARRIER REPORT',
    '',
    `Category:        ${type ? `${type.category} — ${category.label}` : 'Not specified'}`,
    `Barrier type:    ${type ? type.label : 'Not specified'}`,
    `Date encountered: ${barrier.date || 'Not specified'}`,
    `Location:        ${barrier.location || 'Not specified'}`,
    `Corridor:        ${barrier.corridor || 'Not specified'}`,
    `Agency involved: ${barrier.agency || 'Not specified'}`,
    ''
  ];

  if (barrier.goods) lines.push(`Goods:           ${barrier.goods}`, '');

  lines.push('IMPACT');
  if (Number.isFinite(barrier.hoursLost) && barrier.hoursLost > 0) {
    lines.push(`Time lost:       ${barrier.hoursLost} hours`);
  }
  if (Number.isFinite(barrier.costUsd) && barrier.costUsd > 0) {
    lines.push(`Direct cost:     USD ${barrier.costUsd.toFixed(2)}`);
  }
  lines.push(`Recurring:       ${barrier.recurring ? 'Yes — encountered repeatedly' : 'First occurrence reported'}`);
  lines.push('');

  lines.push('DESCRIPTION');
  lines.push(barrier.description || 'Not provided.');
  lines.push('');

  if (barrier.resolutionSought) {
    lines.push('RESOLUTION SOUGHT');
    lines.push(barrier.resolutionSought);
    lines.push('');
  }

  lines.push('---');
  lines.push('Prepared with Kanda Logistics Advisory. Lodge at https://tradebarriers.africa');
  lines.push('under the EAC Elimination of Non-Tariff Barriers Act, 2017.');

  return lines.join('\n');
}

/** Aggregate an operator's own barrier log — their private NTB tax. */
export function summarise(barriers) {
  const hours = barriers.reduce((sum, b) => sum + (b.hoursLost || 0), 0);
  const cost = barriers.reduce((sum, b) => sum + (b.costUsd || 0), 0);

  const byLocation = new Map();
  const byType = new Map();

  for (const b of barriers) {
    const loc = b.location || 'Unspecified';
    const prev = byLocation.get(loc) || { count: 0, hours: 0, cost: 0 };
    byLocation.set(loc, {
      count: prev.count + 1,
      hours: prev.hours + (b.hoursLost || 0),
      cost: prev.cost + (b.costUsd || 0)
    });

    const type = barrierType(b.typeId);
    const key = type ? type.label : 'Other';
    byType.set(key, (byType.get(key) || 0) + 1);
  }

  const rank = (map, key) => [...map.entries()].sort((a, b) => (b[1][key] ?? b[1]) - (a[1][key] ?? a[1]));

  return {
    count: barriers.length,
    hours,
    cost,
    topLocations: rank(byLocation, 'hours').slice(0, 5),
    topTypes: [...byType.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5)
  };
}
